import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";

import { zoneService } from "/app/src/services";
import { Search } from "/app/src/components/generic/components/search";
import { useTranslation } from "react-i18next";
import { Zone as ZoneType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
/**
 * Renders the Zones component displaying zones data.
 *
 * @returns {JSX.Element} The rendered Zones component.
 */
export default function Zones() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sort, setSort] = useSortUpgrade([]);
  const [searchParams] = useSearchParams();

  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const columnHelper = createColumnHelper<ZoneType>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: () => t("translation:name"),
      }),
      columnHelper.accessor("typeDescription", {
        id: "description",
        cell: (info) => info.getValue(),
        header: () => t("translation:description"),
      }),
      columnHelper.accessor("pickTypeDescription", {
        id: "pickTypeDescription",
        cell: (info) => info.getValue(),
        header: () => t("translation:pick_type"),
      }),
      columnHelper.accessor("putTypeDescription", {
        id: "putTypeDescription",
        cell: (info) => info.getValue(),
        header: () => t("translation:put_type"),
      }),
    ],
    [columnHelper, t],
  );

  const gotToZone = useCallback(
    (id: number) => {
      navigate(`/explorer/zones/${id}`);
    },
    [navigate],
  );

  const {
    data: zones,
    count: zonesCount,
    isFetching,
  } = usePaginatedData<ZoneType>({
    queryKey: ["zones"],
    searchString,
    sort,
    page,
    pageSize,
    service: zoneService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: zonesCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:zones")} - ItemPath</title>
      </Helmet>
      <Search allowClear setSearchString={setSearchString} />
      <Table
        loading={isFetching}
        rows={zones}
        tableColumns={columns}
        length={zonesCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        enableRowSelection
        rowClicked={gotToZone}
        emptyText={`${t("translation:no")} ${t("translation:zones")} ${t(
          "translation:found",
        )}`}
      />
    </div>
  );
}
